import React, { useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts'

import GraphModal from 'components/GraphModal'

import { SMOOTH_ICON_SX } from './styles'
import { Widget } from './Widget'
import {
  buildHistoryGraphData,
  buildLineData,
  createGraphTimeRangeParams,
  formatDecimal,
  formatGraphTick,
  formatGraphTimeTick,
  getGraphTimeTicks
} from '../utils'

const formatTooltipTime = (timestamp) => {
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  }).replace('.', '')
}

const GraphTooltip = ({ active, payload, unit, showSeriesNames }) => {
  if (!active || !payload?.length) return null
  const timestamp = payload[0]?.payload?.timestampMs
  return (
    <Stack sx={{ backgroundColor: '#292929', color: '#fff', borderRadius: '6px', px: 1, py: 0.5, gap: 0.25 }}>
      {timestamp && (
        <Typography component="span" sx={{ color: '#fff', fontSize: 10, fontWeight: 500, lineHeight: '12px' }}>
          {formatTooltipTime(timestamp)}
        </Typography>
      )}
      {payload.map(item => (
        <Stack key={item.dataKey} direction="row" alignItems="center" gap={0.75}>
          <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: item.color, flexShrink: 0 }} />
          <Typography component="span" sx={{ color: '#fff', fontSize: 10, fontWeight: 500, lineHeight: '12px' }}>
            {showSeriesNames && item.name ? `${item.name} : ` : ''}{formatDecimal(item.value, 1)}{unit || ''}
          </Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export const EquipmentLineChart = ({ data, color, series, height = 180, isExpanded = false, unit, percentage = false, showLegend = false }) => {
  const chartSeries = series?.length ? series : [{ key: 'value', color }]
  const isPercentage = percentage || unit === '%'
  const max = Math.max(...data.flatMap(point => chartSeries.map(({ key }) => Number(point[key]) || 0)), 0)
  const topTick = isPercentage ? 100 : max > 0 ? Math.ceil(max) : 1
  const middleTick = topTick / 2
  const yTicks = [0, middleTick, topTick]
  const timeTicks = getGraphTimeTicks(data)
  const chartHeight = isExpanded ? 500 : height
  const latestPoint = [...data].reverse().find(point => chartSeries.some(({ key }) => point[key] != null))

  return (
    <Stack sx={{ width: '100%', height: chartHeight, minHeight: 0 }} gap={1}>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 14, right: 8, bottom: 4, left: 0 }}>
            <XAxis
              dataKey="timestampMs"
              type="number"
              scale="time"
              domain={['dataMin', 'dataMax']}
              ticks={timeTicks}
              axisLine={{ stroke: '#EFEFEF' }}
              tickLine={false}
              tickFormatter={timestamp => formatGraphTimeTick(timestamp, data)}
              tick={{ fill: '#989898', fontSize: 10, fontWeight: 400 }}
              interval={0}
              minTickGap={16}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, topTick]}
              ticks={yTicks}
              tickFormatter={value => `${formatGraphTick(value)}${unit || ''}`}
              tick={{ fill: '#989898', fontSize: 10, fontWeight: 400 }}
              width={46}
            />
            <ReferenceLine y={0} stroke="#EFEFEF" strokeWidth={1} />
            <ReferenceLine y={middleTick} stroke="#EFEFEF" strokeWidth={1} />
            <RechartsTooltip content={<GraphTooltip unit={unit} showSeriesNames={Boolean(series?.length)} />} cursor={{ stroke: '#EFEFEF' }} />
            {chartSeries.map(item => (
              <Area
                key={item.key}
                type="monotone"
                dataKey={item.key}
                name={item.name}
                stroke={item.color}
                strokeWidth={isExpanded ? 2.5 : 2}
                fill={item.color}
                fillOpacity={0.14}
                dot={false}
                connectNulls={false}
                isAnimationActive
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </Box>
      {showLegend && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" gap={2} flexWrap="wrap">
          {chartSeries.map(item => (
            <Stack key={item.key} direction="row" alignItems="center" gap={0.5}>
              <Box sx={{ width: 12, height: 3, borderRadius: '3px', backgroundColor: item.color }} />
              <Typography sx={{ color: '#656565', fontSize: 10, fontWeight: 500 }}>
                {item.name || item.key}{latestPoint?.[item.key] != null ? ` : ${formatDecimal(latestPoint[item.key], 1)}${unit || ''}` : ''}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  )
}

const EquipmentGraphModal = ({
  open,
  onClose,
  title,
  data,
  color,
  series,
  unit,
  percentage,
  showLegend,
  modalQuery,
  modalVariables,
  mapModalData
}) => {
  const { t } = useTranslation()
  const [modalTimeRangePreset, setModalTimeRangePreset] = useState('-1d')
  const [modalDateRange, setModalDateRange] = useState({ start: null, end: null })
  const [isModalCustomRange, setIsModalCustomRange] = useState(false)
  const timeRange = createGraphTimeRangeParams(modalTimeRangePreset, modalDateRange, isModalCustomRange)
  const variables = {
    ...modalVariables,
    timeRange
  }
  const { data: modalData, loading: modalLoading } = useQuery(modalQuery, {
    variables,
    skip: !open,
    fetchPolicy: 'network-only'
  })
  const modalGraph = mapModalData ? mapModalData(modalData) : { data, series }

  const handleTimeRangeChange = (range) => {
    setModalTimeRangePreset(range)
    setIsModalCustomRange(false)
    setModalDateRange({ start: null, end: null })
  }

  const handleDateRangeSubmit = (start, end) => {
    if (!start || !end) return
    setModalDateRange({ start, end })
    setIsModalCustomRange(true)
    setModalTimeRangePreset('')
  }

  const handleDateRangeReset = () => {
    setModalDateRange({ start: null, end: null })
    setIsModalCustomRange(false)
    setModalTimeRangePreset('-1d')
  }

  return (
    <GraphModal
      open={open}
      onClose={onClose}
      title={title}
      timeRangePreset={modalTimeRangePreset}
      isCustomRange={isModalCustomRange}
      dateRange={modalDateRange}
      onTimeRangeChange={handleTimeRangeChange}
      onStartDateChange={value => setModalDateRange(prev => ({ ...prev, start: value }))}
      onEndDateChange={value => setModalDateRange(prev => ({ ...prev, end: value }))}
      onDateRangeSubmit={handleDateRangeSubmit}
      onDateRangeReset={handleDateRangeReset}
    >
      <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        {modalLoading ? (
          <Typography sx={{ color: '#989898', fontSize: 12 }}>{t('ui_elements.loading')}</Typography>
        ) : (
          <EquipmentLineChart
            data={modalGraph.data || []}
            color={color}
            series={modalGraph.series || series}
            unit={unit}
            percentage={percentage}
            showLegend={showLegend}
            isExpanded
          />
        )}
      </Box>
    </GraphModal>
  )
}

export const EquipmentGraphWidget = ({ title, data, color, series, loading, sx, chartHeight = 180, unit, percentage, showLegend, modalQuery, modalVariables, mapModalData }) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation()

  return (
    <>
      <Widget
        title={title}
        action={(
          <Box
            onClick={() => setOpen(true)}
            sx={{ width: 24, height: 24, borderRadius: '50%', border: '1px solid #EFEFEF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#464646', cursor: 'pointer', flexShrink: 0 }}
          >
            <Box component="i" className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" sx={{ fontSize: 10, ...SMOOTH_ICON_SX }} />
          </Box>
        )}
        sx={{ flex: 1, minHeight: 260, ...sx }}
      >
        {loading ? (
          <Typography sx={{ color: '#989898', fontSize: 12 }}>{t('ui_elements.loading')}</Typography>
        ) : (
          <Box sx={{ flex: 1, minHeight: typeof chartHeight === 'number' ? chartHeight : 0 }}>
            <EquipmentLineChart data={data} color={color} series={series} height={chartHeight} unit={unit} percentage={percentage} showLegend={showLegend} />
          </Box>
        )}
      </Widget>
      {modalQuery && (
        <EquipmentGraphModal
          open={open}
          onClose={() => setOpen(false)}
          title={title}
          data={data}
          color={color}
          series={series}
          unit={unit}
          percentage={percentage}
          showLegend={showLegend}
          modalQuery={modalQuery}
          modalVariables={modalVariables}
          mapModalData={mapModalData}
        />
      )}
    </>
  )
}

export const HistoryAvailabilityGraphWidget = ({ history, title, loading, sx, modalQuery, modalVariables, modalDataKey }) => {
  const graphData = useMemo(() => buildLineData({
    timestamps: history?.timestamps || [],
    values: history?.status?.map(value => value == null ? null : value ? 100 : 0) || []
  }), [history])

  return (
    <EquipmentGraphWidget
      title={title}
      data={graphData}
      color="#A5D9CB"
      unit="%"
      percentage
      loading={loading}
      sx={sx}
      modalQuery={modalQuery}
      modalVariables={modalVariables}
      mapModalData={data => ({ data: buildHistoryGraphData(data?.[modalDataKey]) })}
    />
  )
}
